import { useEffect, useState } from "react";
import { Bot, Download, Loader2, Mail, Megaphone, Send, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import AdminGazetteAgent from "@/pages/admin/AdminGazetteAgent";

const inputClass = "w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-3 text-sm outline-none transition-colors focus:border-amber-deep";

const FREQUENCY_LABELS = { weekly: "1 fois / semaine", monthly: "1 fois / mois", yearly: "1 fois / an" };
const FREQUENCY_STYLES = { weekly: "bg-terra/10 text-terra", monthly: "bg-amber-50 text-amber-deep", yearly: "bg-sage-light text-sage" };

const TABS = [
  { id: "agent", label: "Agent IA", icon: Bot, testid: "nl-tab-agent" },
  { id: "nouvelle", label: "Nouvelle campagne", icon: Megaphone, testid: "nl-tab-nouvelle" },
  { id: "abonnes", label: "Abonnés", icon: Users, testid: "nl-tab-abonnes" },
  { id: "campagnes", label: "Campagnes envoyées", icon: Mail, testid: "nl-tab-campagnes" },
];

export default function AdminNewsletter() {
  const [tab, setTab] = useState("agent");
  const [data, setData] = useState(null);
  const [campaigns, setCampaigns] = useState(null);
  const [form, setForm] = useState({ subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = () => {
    api.get("/admin/newsletter/subscribers").then(({ data }) => setData(data)).catch(() => setData({ subscribers: [], stats: { total: 0, active: 0, by_frequency: {} } }));
    api.get("/admin/newsletter/campaigns").then(({ data }) => setCampaigns(data)).catch(() => setCampaigns([]));
  };
  useEffect(load, []);

  const handleDelete = async (email) => {
    if (!window.confirm(`Supprimer définitivement l'abonné ${email} ?`)) return;
    try {
      await api.delete(`/admin/newsletter/subscribers/${encodeURIComponent(email)}`);
      toast.success("Abonné supprimé");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data: blob } = await api.get("/admin/newsletter/export", { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "abonnes-newsletter.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export impossible, réessayez.");
    } finally {
      setExporting(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const active = data?.stats?.active || 0;
    if (!window.confirm(`Envoyer « ${form.subject} » aux ${active} abonné(s) actif(s) ?`)) return;
    setSending(true);
    try {
      const { data: campaign } = await api.post("/admin/newsletter/campaigns", { ...form, origin_url: window.location.origin });
      toast.success(`Campagne envoyée : ${campaign.sent_count} e-mails distribués${campaign.failed_count ? `, ${campaign.failed_count} échec(s)` : ""}`);
      setForm({ subject: "", message: "" });
      load();
      setTab("campagnes");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const handleDeleteCampaign = async (c) => {
    if (!window.confirm(`Retirer la campagne « ${c.subject} » de l'historique ?`)) return;
    try {
      await api.delete(`/admin/newsletter/campaigns/${c.campaign_id}`);
      toast.success("Campagne retirée de l'historique");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!data) {
    return <p data-testid="newsletter-loading" className="py-20 text-center font-serif text-xl text-bark-soft">Chargement…</p>;
  }

  const { stats } = data;

  return (
    <div data-testid="admin-newsletter" className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-bark">Newsletter</h1>
          <p className="mt-1 text-sm text-bark-soft">La Gazette Champêtre : rédigez une campagne, gérez vos abonnés, suivez vos envois.</p>
        </div>
        {tab === "abonnes" && (
          <button
            data-testid="export-csv-button"
            onClick={handleExport}
            disabled={exporting || stats.active === 0}
            className="flex items-center gap-2 rounded-full border border-amber-800/25 bg-white px-5 py-2.5 text-sm font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep disabled:opacity-50"
          >
            {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Exporter les abonnés (CSV)
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={t.testid}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all ${tab === t.id ? "bg-amber-deep text-white shadow-md shadow-amber-900/15" : "border border-amber-800/20 bg-white text-bark-soft hover:border-amber-deep hover:text-amber-deep"}`}
          >
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "agent" && <AdminGazetteAgent />}

      {tab === "nouvelle" && (
        <form data-testid="campaign-form" onSubmit={handleSend} className="space-y-4 rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
          <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-bark">
            <Megaphone size={18} className="text-honey" /> Nouvelle campagne
          </h2>
          <p className="text-sm text-bark-soft">
            Rédigez votre e-mail — il sera envoyé aux <strong>{stats.active}</strong> abonné(s) actif(s) avec un lien de désinscription automatique.
          </p>
          <div>
            <label htmlFor="campaign-subject" className="mb-1.5 block text-sm font-medium text-bark">Objet de l'e-mail *</label>
            <input
              id="campaign-subject"
              data-testid="campaign-subject"
              required
              minLength={3}
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className={inputClass}
              placeholder="La récolte de lavande est arrivée !"
            />
          </div>
          <div>
            <label htmlFor="campaign-message" className="mb-1.5 block text-sm font-medium text-bark">Message *</label>
            <textarea
              id="campaign-message"
              data-testid="campaign-message"
              required
              minLength={10}
              rows={8}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className={`${inputClass} resize-y`}
              placeholder={"Bonjour,\n\nLa récolte de lavande vient d'arriver à la miellerie…\n\nÀ très vite,\nLa miellerie"}
            />
            <p className="mt-1 text-xs text-stone-mute">Sautez une ligne pour créer un nouveau paragraphe.</p>
          </div>
          <button
            data-testid="campaign-send"
            type="submit"
            disabled={sending || stats.active === 0}
            className="flex items-center gap-2 rounded-full bg-amber-deep px-6 py-3 text-sm font-semibold text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-95 disabled:opacity-60"
          >
            {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            {sending ? "Envoi en cours…" : `Envoyer aux ${stats.active} abonné(s)`}
          </button>
        </form>
      )}

      {tab === "abonnes" && (
        <div data-testid="nl-panel-abonnes" className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-4">
            <div data-testid="newsletter-stat-active" className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
              <Users size={22} className="text-honey" />
              <p className="mt-3 font-serif text-3xl font-semibold text-bark">{stats.active}</p>
              <p className="mt-1 text-xs uppercase tracking-[0.15em] text-stone-mute">Abonnés actifs</p>
            </div>
            {["weekly", "monthly", "yearly"].map((f) => (
              <div key={f} data-testid={`newsletter-stat-${f}`} className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
                <Mail size={22} className="text-honey" />
                <p className="mt-3 font-serif text-3xl font-semibold text-bark">{stats.by_frequency?.[f] || 0}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.15em] text-stone-mute">{FREQUENCY_LABELS[f]}</p>
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
            <h2 className="mb-4 font-serif text-xl font-semibold text-bark">Abonnés ({data.subscribers.length})</h2>
            {data.subscribers.length === 0 ? (
              <p data-testid="newsletter-empty" className="text-sm text-bark-soft">Aucun inscrit pour le moment — le formulaire est en pied de page et sur l'accueil.</p>
            ) : (
              <ul className="divide-y divide-amber-900/10">
                {data.subscribers.map((s) => (
                  <li key={s.email} data-testid={`subscriber-row-${s.email}`} className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-bark">{s.email}</p>
                      <p className="text-xs text-stone-mute">
                        Inscrit le {s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "—"}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span data-testid={`subscriber-frequency-${s.email}`} className={`rounded-full px-3 py-1 text-xs font-medium ${FREQUENCY_STYLES[s.frequency] || "bg-sand text-bark-soft"}`}>
                        {FREQUENCY_LABELS[s.frequency] || s.frequency}
                      </span>
                      {!s.active && (
                        <span data-testid={`subscriber-unsubscribed-${s.email}`} className="rounded-full bg-sand px-3 py-1 text-xs font-medium text-stone-mute">
                          Désinscrit
                        </span>
                      )}
                      <button
                        data-testid={`delete-subscriber-${s.email}`}
                        onClick={() => handleDelete(s.email)}
                        className="flex h-9 w-9 items-center justify-center rounded-full border border-terra/30 text-terra transition-colors hover:bg-terra/10"
                        aria-label={`Supprimer ${s.email}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {tab === "campagnes" && (
        <div data-testid="nl-panel-campagnes" className="space-y-6">
          <div className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
            <h2 className="mb-4 font-serif text-xl font-semibold text-bark">Campagnes envoyées</h2>
            {!campaigns ? (
              <p className="text-sm text-bark-soft">Chargement…</p>
            ) : campaigns.length === 0 ? (
              <p data-testid="campaigns-empty" className="text-sm text-bark-soft">Aucune campagne envoyée pour le moment.</p>
            ) : (
              <ul className="divide-y divide-amber-900/10">
                {campaigns.map((c) => (
                  <li key={c.campaign_id} data-testid={`campaign-row-${c.campaign_id}`} className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium text-bark">{c.subject}</p>
                      <p className="text-xs text-stone-mute">
                        {c.sent_at ? new Date(c.sent_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2 text-xs font-semibold">
                      <span className="rounded-full bg-sage-light px-3 py-1 text-sage">{c.sent_count} envoyés</span>
                      {c.failed_count > 0 && <span className="rounded-full bg-terra/15 px-3 py-1 text-terra">{c.failed_count} échecs</span>}
                      <button
                        data-testid={`delete-campaign-${c.campaign_id}`}
                        onClick={() => handleDeleteCampaign(c)}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-terra/30 text-terra transition-colors hover:bg-terra/10"
                        aria-label={`Retirer ${c.subject}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
